import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { colourHex } from '../lib/format.ts';
import { ChevronDown, ChevronRight, Kebab } from '../lib/icons.tsx';
import AnchoredPop from '../components/AnchoredPop.tsx';
import { useDismissMenu } from '../lib/useDismissMenu.ts';
import { cssVars } from '../lib/css.ts';
import AccentSwatches from './AccentSwatches.tsx';
import type { SectionRef } from '../../shared/deck.ts';

/**
 * Sticky group header inside the filmstrip: colour dot (click → swatch
 * popover), section name (double-click → inline rename), slide count, and a
 * kebab for rename / move / add-slide / delete. Collapse is opt-in and
 * persisted by the filmstrip.
 */

type SectionHeaderProps = {
  section: SectionRef;
  count: number;
  collapsed?: boolean;
  onToggleCollapse: () => void;
  /** The drag grip, when this collapsed section can be moved. */
  dragHandle?: ReactNode;
  onRename: (name: string) => void;
  onRecolour: (color: string) => void;
  /** Opens the add-slide chooser for this section. */
  onAddSlide: () => void;
  /** Null at the top of the deck, and for intro/closing. */
  onMoveUp?: (() => void) | null;
  /** Null at the bottom of the deck, and for intro/closing. */
  onMoveDown?: (() => void) | null;
  /** Show the move items at all (content section, 2+ sections). */
  movable?: boolean;
  /** Null when the section can't be deleted (intro/closing). */
  onDelete?: (() => void) | null;
  busy?: boolean;
};

export default function SectionHeader({
  section,
  count,
  collapsed,
  onToggleCollapse,
  dragHandle,
  onRename,
  onRecolour,
  onAddSlide,
  onMoveUp,
  onMoveDown,
  movable,
  onDelete,
  busy,
}: SectionHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.name || section.key);
  const [popover, setPopover] = useState<'swatches' | 'menu' | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dotRef = useRef<HTMLButtonElement>(null);
  const kebabRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(section.name || section.key);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close popovers on outside click / Escape / a click into the preview iframe.
  // Both refs count as inside: the popover is portalled out of the header now
  // (AnchoredPop), so the header alone no longer contains it and every click on
  // a swatch or a menu item would read as an outside click.
  const closePopover = useCallback(() => setPopover(null), []);
  const insideRefs = useMemo(() => [rootRef, popRef], []);
  useDismissMenu(insideRefs, closePopover, !!popover);

  function commitRename() {
    setEditing(false);
    const name = draft.trim();
    if (name && name !== section.name) onRename(name);
  }

  return (
    <div className={'strip-section' + (dragHandle ? ' strip-section--draggable' : '')} ref={rootRef}>
      {dragHandle}
      <button
        type="button"
        className="strip-section__collapse iconbtn"
        onClick={onToggleCollapse}
        title={collapsed ? 'Show this section’s slides' : 'Collapse this section'}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
      </button>
      <button
        type="button"
        ref={dotRef}
        className="strip-section__dot"
        style={cssVars({ '--swatch': colourHex(section.color || 'ORANGE') })}
        onClick={() => setPopover(popover === 'swatches' ? null : 'swatches')}
        title="Change the section colour"
        aria-label={'Section colour: ' + (section.color || 'ORANGE').toLowerCase()}
        disabled={busy}
      />
      {editing ? (
        <input
          ref={inputRef}
          className="strip-section__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitRename();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
          }}
          maxLength={80}
        />
      ) : (
        <span
          className="strip-section__name"
          onDoubleClick={() => setEditing(true)}
          title={(section.name || section.key) + ' (double-click to rename)'}
        >
          {section.n ? section.n + ' · ' : ''}
          {section.name || section.key}
        </span>
      )}
      {collapsed && count > 0 && <span className="strip-section__count">{count}</span>}
      <button
        type="button"
        ref={kebabRef}
        className="strip-section__kebab iconbtn"
        onClick={() => setPopover(popover === 'menu' ? null : 'menu')}
        title="Section options"
        aria-label="Section options"
        aria-haspopup="menu"
      >
        <Kebab size={13} />
      </button>

      {popover === 'swatches' && (
        <AnchoredPop anchor={dotRef} popRef={popRef} className="strip-pop strip-pop--swatches">
          <AccentSwatches
            value={section.color}
            onPick={(token, meta) => {
              // Keep the popover open for a custom pick — closing it unmounts
              // the colour input mid-drag and loses the gesture.
              if (!meta?.custom) setPopover(null);
              if (token !== section.color) onRecolour(token);
            }}
            disabled={busy}
          />
        </AnchoredPop>
      )}
      {popover === 'menu' && (
        <AnchoredPop anchor={kebabRef} popRef={popRef} className="strip-pop strip-pop--menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="strip-pop__item"
            onClick={() => {
              setPopover(null);
              setEditing(true);
            }}
          >
            Rename section
          </button>
          {movable && (
            <>
              <button
                type="button"
                role="menuitem"
                className="strip-pop__item"
                disabled={busy || !onMoveUp}
                onClick={() => {
                  setPopover(null);
                  onMoveUp?.();
                }}
              >
                Move section up
              </button>
              <button
                type="button"
                role="menuitem"
                className="strip-pop__item"
                disabled={busy || !onMoveDown}
                onClick={() => {
                  setPopover(null);
                  onMoveDown?.();
                }}
              >
                Move section down
              </button>
            </>
          )}
          <button
            type="button"
            role="menuitem"
            className="strip-pop__item"
            onClick={() => {
              setPopover(null);
              onAddSlide();
            }}
          >
            Add a slide here…
          </button>
          {onDelete && (
            <button
              type="button"
              role="menuitem"
              className="strip-pop__item strip-pop__item--danger"
              onClick={() => {
                setPopover(null);
                onDelete();
              }}
            >
              Delete section…
            </button>
          )}
        </AnchoredPop>
      )}
    </div>
  );
}
